import { useState, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Clock, CalendarIcon, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { pb } from '@/lib/pocketbase';
import { EmailService } from '@/services/emailService';
import { notificationService } from '@/services/notificationService';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';

interface RescheduleRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  booking: any;
}

export function RescheduleRequestModal({ isOpen, onClose, booking }: RescheduleRequestModalProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{ start: string; end: string } | null>(null);
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<Array<{ start: string; end: string }>>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  
  // Manual time input for tutors
  const [manualStartTime, setManualStartTime] = useState('');
  const [manualEndTime, setManualEndTime] = useState('');

  // State for tutor availability and role detection
  const [tutorAvailability, setTutorAvailability] = useState<any>(null);
  const [tutorUserId, setTutorUserId] = useState<string | null>(null);
  
  // Determine if user is tutor or student for this booking
  // Key: tutorUserId is fetched from tutor profile, not from booking.tutorId
  // If current user's ID matches tutorUserId → they are the tutor
  // Otherwise → they are a student (could be group leader or member)
  const isTutor = tutorUserId && tutorUserId === user?.id;
  const isStudent = !isTutor; // Anyone who is not the tutor is a student
  const userRole = isTutor ? 'tutor' : 'student';
  const otherPartyRole = isTutor ? 'student' : 'tutor';

  // Fetch tutor profile and availability when modal opens
  useEffect(() => {
    const fetchTutorData = async () => {
      if (!booking?.tutorId) return;
      
      try {
        const tutorProfile = await pb.collection('tutors').getOne(booking.tutorId);
        setTutorUserId(tutorProfile.user); // Store tutor's user ID for role detection
        setTutorAvailability(tutorProfile.availability);
      } catch (error) {
        console.error('Error fetching tutor data:', error);
      }
    };

    if (isOpen && booking) {
      fetchTutorData();
    }
  }, [isOpen, booking]);

  // Check if tutor is available on a given date (for students only)
  const isTutorAvailableOnDate = (date: Date): boolean => {
    // Tutors can select any date
    if (userRole === 'tutor') return true;
    
    // Students must select dates where tutor is available
    if (!tutorAvailability?.timeSlots) return false;
    
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
    const daySlot = tutorAvailability.timeSlots.find((slot: any) => slot.day === dayName);
    
    return daySlot && daySlot.slots && daySlot.slots.length > 0;
  };

  // Convert time from tutor's timezone to viewer's timezone
  const convertTimeToViewerTimezone = (timeString: string) => {
    if (!timeString || !booking?.tutorTimezone || !booking?.studentTimezone) {
      return timeString;
    }
    
    // If timezones match, no conversion needed
    if (booking.tutorTimezone === booking.studentTimezone) {
      return timeString;
    }
    
    try {
      // Times are stored in tutor's timezone
      // - Tutors see original time
      // - Students see converted time
      if (userRole === 'tutor') {
        return timeString;
      }
      
      // Extract date properly
      let dateString: string;
      if (booking.date.includes('T')) {
        dateString = booking.date.split('T')[0];
      } else if (booking.date.includes(' ')) {
        dateString = booking.date.split(' ')[0];
      } else {
        dateString = booking.date;
      }
      
      const dateTimeString = `${dateString}T${timeString}:00`;
      
      // Convert from tutor's timezone to student's timezone
      const tutorDate = fromZonedTime(dateTimeString, booking.tutorTimezone);
      const studentDate = toZonedTime(tutorDate, booking.studentTimezone);
      const hours = studentDate.getHours().toString().padStart(2, '0');
      const minutes = studentDate.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch (error) {
      console.error('Error converting time:', error);
      return timeString;
    }
  };

  // Get viewer's timezone
  const viewerTimezone = userRole === 'tutor' ? booking?.tutorTimezone : booking?.studentTimezone;

  // Get tomorrow's date (minimum date for reschedule)
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow;
  };

  // Fetch tutor's available slots for selected date
  const fetchAvailableSlotsForDate = useCallback(async (date: Date) => {
    if (!booking?.tutorId) return;
    
    setIsLoadingSlots(true);
    try {
      // Fetch tutor profile to get availability
      const tutorProfile = await pb.collection('tutors').getOne(booking.tutorId);
      
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
      const daySlot = tutorProfile.availability?.timeSlots?.find((slot: any) => slot.day === dayName);
      
      if (daySlot?.slots) {
        setAvailableTimeSlots(daySlot.slots);
      } else {
        setAvailableTimeSlots([]);
      }
    } catch (error) {
      console.error('Error fetching available slots:', error);
      setAvailableTimeSlots([]);
    } finally {
      setIsLoadingSlots(false);
    }
  }, [booking?.tutorId]);

  useEffect(() => {
    // Only fetch availability slots for students
    // Tutors can manually input any time
    if (selectedDate && userRole === 'student') {
      fetchAvailableSlotsForDate(selectedDate);
    } else {
      setAvailableTimeSlots([]);
    }
  }, [selectedDate, fetchAvailableSlotsForDate, userRole]);

  const handleSubmit = async () => {
    // Validation based on user role
    if (!selectedDate) {
      toast({
        title: "Missing Information",
        description: "Please select a date",
        variant: "destructive"
      });
      return;
    }
    
    // For tutors using manual input
    if (userRole === 'tutor' && (!manualStartTime || !manualEndTime)) {
      toast({
        title: "Missing Information",
        description: "Please enter both start and end times",
        variant: "destructive"
      });
      return;
    }
    
    // For students or tutors using slot selection
    if (userRole === 'student' && !selectedTimeSlot) {
      toast({
        title: "Missing Information",
        description: "Please select a time slot",
        variant: "destructive"
      });
      return;
    }
    
    // Validate time format and logic for manual input
    if (userRole === 'tutor' && manualStartTime && manualEndTime) {
      const startParts = manualStartTime.split(':');
      const endParts = manualEndTime.split(':');
      
      if (startParts.length !== 2 || endParts.length !== 2) {
        toast({
          title: "Invalid Time Format",
          description: "Please use HH:MM format (e.g., 09:00)",
          variant: "destructive"
        });
        return;
      }
      
      const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
      const endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
      
      if (startMinutes >= endMinutes) {
        toast({
          title: "Invalid Time Range",
          description: "End time must be after start time",
          variant: "destructive"
        });
        return;
      }
    }

    setIsSubmitting(true);
    let rescheduleRequest: any = null;
    
    try {
      // Cancel all previous pending requests for this booking
      const existingRequests = await pb.collection('reschedule_requests').getList(1, 100, {
        filter: `booking_id="${booking.id}" && status="pending"`
      });
      
      for (const request of existingRequests.items) {
        await pb.collection('reschedule_requests').update(request.id, {
          status: 'cancelled'
        });
      }

      // Generate unique approval token
      const approvalToken = crypto.randomUUID();

      // Get tutor and student information from booking
      const tutorProfile = await pb.collection('tutors').getOne(booking.tutorId);
      
      // Get student email directly from booking record
      const studentEmail = booking.studentEmail;
      
      console.log('📧 Fetched data:', {
        tutorEmail: tutorProfile.personalEmail,
        studentEmail,
        isStudent,
        currentUserId: user!.id,
        tutorUserId: tutorProfile.user
      });
      
      // Determine who is requesting and who should approve
      // IMPORTANT: Use tutorProfile.user (user ID), not booking.tutorId (tutor profile ID)
      const otherPartyId = isStudent ? tutorProfile.user : booking.studentId;
      const otherPartyEmail = isStudent 
        ? tutorProfile.personalEmail 
        : studentEmail;
      const otherPartyName = isStudent 
        ? tutorProfile.name 
        : user!.name;

      // Get current user's email
      const currentUserEmail = isStudent 
        ? studentEmail 
        : tutorProfile.personalEmail;
      const currentUserName = user!.name || 'User';
      
      console.log('📧 Email addresses:', {
        otherPartyEmail,
        otherPartyName,
        otherPartyRole: otherPartyRole,
        currentUserEmail,
        currentUserName
      });
      
      // Validate emails before proceeding
      if (!otherPartyEmail) {
        toast({
          title: "Email Missing",
          description: `Cannot send reschedule request - ${otherPartyRole}'s email is not available`,
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }
      
      if (!currentUserEmail) {
        toast({
          title: "Email Missing",
          description: "Cannot send confirmation - your email is not available",
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }

      // Determine time slot based on user role
      const finalTimeSlot = userRole === 'tutor' && manualStartTime && manualEndTime
        ? { start: manualStartTime, end: manualEndTime }
        : selectedTimeSlot!;

      // Create reschedule request
      console.log('📝 Creating reschedule request with data:', {
        booking_id: booking.id,
        requested_by: user!.id,
        requested_by_role: userRole,
        requested_to: otherPartyId,
        requested_to_role: otherPartyRole,
        new_date: selectedDate.toISOString().split('T')[0],
        new_start_time: finalTimeSlot.start,
        new_end_time: finalTimeSlot.end,
        approval_token: approvalToken
      });
      
      const rescheduleData = {
        booking_id: booking.id,
        requested_by: user!.id,
        requested_by_role: userRole,
        requested_to: otherPartyId,
        requested_to_role: otherPartyRole,
        new_date: selectedDate.toISOString().split('T')[0],
        new_start_time: finalTimeSlot.start,
        new_end_time: finalTimeSlot.end,
        reason: reason.trim() || 'No reason provided',
        status: 'pending',
        approval_token: approvalToken
      };

      try {
        rescheduleRequest = await pb.collection('reschedule_requests').create(rescheduleData);
        console.log('✅ Reschedule request created:', rescheduleRequest.id);
      } catch (createError) {
        console.error('❌ Failed to create reschedule request:', createError);
        setIsSubmitting(false);
        toast({
          title: "Error",
          description: "Failed to create reschedule request. Please try again.",
          variant: "destructive"
        });
        return; // Exit early if creation fails
      }

      // Send email notification using existing email service
      const approvalLink = `https://tutornesta.com/reschedule-approval/${approvalToken}`;
      
      // Convert times for email based on recipient timezone
      // Original times for the approver (other party)
      const approverOriginalStart = otherPartyRole === 'student' 
        ? convertTimeToViewerTimezone(booking.startTime)
        : booking.startTime;
      const approverOriginalEnd = otherPartyRole === 'student'
        ? convertTimeToViewerTimezone(booking.endTime)
        : booking.endTime;
      
      // New times for the approver (other party)
      const approverNewStart = otherPartyRole === 'student'
        ? convertTimeToViewerTimezone(finalTimeSlot.start)
        : finalTimeSlot.start;
      const approverNewEnd = otherPartyRole === 'student'
        ? convertTimeToViewerTimezone(finalTimeSlot.end)
        : finalTimeSlot.end;
      
      // Original times for the requester (current user)
      const requesterOriginalStart = userRole === 'student'
        ? convertTimeToViewerTimezone(booking.startTime)
        : booking.startTime;
      const requesterOriginalEnd = userRole === 'student'
        ? convertTimeToViewerTimezone(booking.endTime)
        : booking.endTime;
      
      // New times for the requester (current user)
      const requesterNewStart = userRole === 'student'
        ? convertTimeToViewerTimezone(finalTimeSlot.start)
        : finalTimeSlot.start;
      const requesterNewEnd = userRole === 'student'
        ? convertTimeToViewerTimezone(finalTimeSlot.end)
        : finalTimeSlot.end;
      
      // Format dates properly for emails and notifications
      // Parse the booking date properly (could be in various formats)
      let bookingDateObj: Date;
      if (typeof booking.date === 'string') {
        // Handle "YYYY-MM-DD HH:MM:SS.sssZ" or "YYYY-MM-DD" format
        const dateStr = booking.date.split(' ')[0].split('T')[0]; // Get just the date part
        bookingDateObj = new Date(dateStr + 'T00:00:00');
      } else {
        bookingDateObj = new Date(booking.date);
      }
      
      const originalDateFormatted = bookingDateObj.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
      
      const newDateFormatted = selectedDate.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
      
      // Send in-app notification to the person who needs to approve
      console.log('🔔 Sending in-app notification to:', otherPartyId, otherPartyName);
      try {
        await notificationService.sendNotification({
          userId: otherPartyId,
          type: 'general',
          title: '🔄 Reschedule Request',
          message: `${currentUserName} (${userRole}) has requested to reschedule your ${booking.subject || 'session'} from ${originalDateFormatted} ${approverOriginalStart}-${approverOriginalEnd} to ${newDateFormatted} ${approverNewStart}-${approverNewEnd}`,
          data: {
            bookingId: booking.id,
            rescheduleRequestId: rescheduleRequest.id,
            approvalLink: approvalLink,
            originalDate: booking.date,
            originalTime: `${approverOriginalStart} - ${approverOriginalEnd}`,
            newDate: selectedDate.toISOString().split('T')[0],
            newTime: `${approverNewStart} - ${approverNewEnd}`,
            requesterName: currentUserName,
            requesterRole: userRole
          }
        });
        console.log('✅ In-app notification sent');
      } catch (notifError) {
        console.error('❌ In-app notification failed:', notifError);
        // Don't block the flow if notification fails
      }
      
      // Send approval request email to the other party (person who needs to approve)
      console.log('📧 Sending approval email to:', otherPartyEmail, otherPartyName);
      
      await EmailService.sendRescheduleNotification({
        recipientEmail: otherPartyEmail,
        recipientName: otherPartyName,
        requesterName: currentUserName,
        requesterRole: userRole,
        originalDate: originalDateFormatted,
        originalTime: `${approverOriginalStart} - ${approverOriginalEnd}`,
        newDate: newDateFormatted,
        newTime: `${approverNewStart} - ${approverNewEnd}`,
        subject: booking.subject || 'Session',
        reason: reason.trim() || 'No reason provided',
        approvalLink,
        recipientTimezone: otherPartyRole === 'student' ? booking.studentTimezone : booking.tutorTimezone
      });
      console.log('✅ Approval email sent to', otherPartyRole);

      // Send confirmation email to the requester (person who submitted the request)
      console.log('📧 Sending confirmation email to:', currentUserEmail, currentUserName);
      try {
        const confirmResult = await EmailService.sendRescheduleRequestConfirmation({
          recipientEmail: currentUserEmail,
          recipientName: currentUserName,
          recipientRole: userRole,
          approverRole: otherPartyRole,
          originalDate: originalDateFormatted,
          originalTime: `${requesterOriginalStart} - ${requesterOriginalEnd}`,
          newDate: newDateFormatted,
          newTime: `${requesterNewStart} - ${requesterNewEnd}`,
          subject: booking.subject || 'Session',
          reason: reason.trim() || 'No reason provided',
          recipientTimezone: userRole === 'student' ? booking.studentTimezone : booking.tutorTimezone
        });
        
        if (confirmResult.success) {
          console.log('✅ Confirmation email sent to requester');
        } else {
          console.error('❌ Confirmation email failed:', confirmResult.error);
        }
      } catch (emailError) {
        console.error('❌ Email sending failed:', emailError);
        // Don't block the flow if email fails
      }

      toast({
        title: "Reschedule Request Sent",
        description: `Both you and the ${otherPartyRole} will receive confirmation emails. They need to approve the request.`
      });

      onClose();
    } catch (error: any) {
      console.error('❌ Error creating reschedule request:', error);
      
      // Check if error is specifically from reschedule request creation
      const errorMessage = error?.message || error?.toString() || 'Unknown error';
      
      // Only show error if reschedule request creation actually failed
      if (errorMessage.includes('reschedule_requests') || !rescheduleRequest) {
        toast({
          title: "Error",
          description: "Failed to send reschedule request. Please try again.",
          variant: "destructive"
        });
      } else {
        // If reschedule was created but notifications/emails failed, still show success
        toast({
          title: "Reschedule Request Sent",
          description: `Request sent successfully. The ${otherPartyRole} will be notified.`
        });
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Request Reschedule</DialogTitle>
          <DialogDescription>
            Select a new date and time for your session. The {otherPartyRole} will receive an email to approve or reject this request.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Current Booking Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Current Booking</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                <span>{new Date(booking.date).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <div className="flex flex-col gap-1">
                  <span>
                    {convertTimeToViewerTimezone(booking.startTime)} - {convertTimeToViewerTimezone(booking.endTime)}
                  </span>
                  {viewerTimezone && (
                    <span className="text-xs text-muted-foreground">
                      ({viewerTimezone})
                    </span>
                  )}
                  {booking.tutorTimezone && booking.studentTimezone && booking.tutorTimezone !== booking.studentTimezone && userRole === 'student' && (
                    <span className="text-xs text-muted-foreground">
                      Tutor's time: {booking.startTime} - {booking.endTime} ({booking.tutorTimezone})
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Subject:</span>
                <span>{booking.subject}</span>
              </div>
            </CardContent>
          </Card>

          {/* New Date Selection */}
          <div className="space-y-4">
            <Label>Select New Date *</Label>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => {
                setSelectedDate(date);
                setSelectedTimeSlot(null);
              }}
              disabled={(date) => {
                const tomorrow = getTomorrowDate();
                // Tutors can select any future date
                if (userRole === 'tutor') {
                  return date < tomorrow;
                }
                // Students can only select dates where tutor is available
                return date < tomorrow || !isTutorAvailableOnDate(date);
              }}
              className="rounded-md border pointer-events-auto"
            />
            {selectedDate && (
              <div className="text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="h-3 w-3" />
                Selected: {selectedDate.toLocaleDateString()}
              </div>
            )}
            {userRole === 'student' && (
              <div className="text-xs text-muted-foreground">
                Only dates where the tutor is available are shown
              </div>
            )}
          </div>

          {/* Time Slot Selection */}
          {selectedDate && (
            <div className="space-y-4">
              <Label>Select New Time *</Label>
              
              {/* Manual Time Input for Tutors */}
              {userRole === 'tutor' && (
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800 mb-3">
                      As a tutor, you can choose any time for this reschedule
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="start-time" className="text-sm">Start Time</Label>
                        <input
                          id="start-time"
                          type="time"
                          value={manualStartTime}
                          onChange={(e) => setManualStartTime(e.target.value)}
                          className="w-full mt-1 px-3 py-2 border rounded-md"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="end-time" className="text-sm">End Time</Label>
                        <input
                          id="end-time"
                          type="time"
                          value={manualEndTime}
                          onChange={(e) => setManualEndTime(e.target.value)}
                          className="w-full mt-1 px-3 py-2 border rounded-md"
                          required
                        />
                      </div>
                    </div>
                    {manualStartTime && manualEndTime && (
                      <div className="text-sm text-green-600 flex items-center gap-1 mt-3">
                        <CheckCircle className="h-3 w-3" />
                        Selected: {manualStartTime} - {manualEndTime}
                        {viewerTimezone && <span className="ml-1">({viewerTimezone})</span>}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Slot Selection for Students */}
              {userRole === 'student' && (
                <>
                  {isLoadingSlots ? (
                    <div className="text-center py-4 text-muted-foreground">
                      Loading available time slots...
                    </div>
                  ) : availableTimeSlots.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {availableTimeSlots.map((slot, index) => {
                    // Convert slot times to viewer's timezone for display
                    const displayStart = userRole === 'student' && booking?.tutorTimezone && booking?.studentTimezone && booking.tutorTimezone !== booking.studentTimezone
                      ? (() => {
                          try {
                            const dateString = selectedDate.toISOString().split('T')[0];
                            const dateTimeString = `${dateString}T${slot.start}:00`;
                            const tutorDate = fromZonedTime(dateTimeString, booking.tutorTimezone);
                            const studentDate = toZonedTime(tutorDate, booking.studentTimezone);
                            const hours = studentDate.getHours().toString().padStart(2, '0');
                            const minutes = studentDate.getMinutes().toString().padStart(2, '0');
                            return `${hours}:${minutes}`;
                          } catch {
                            return slot.start;
                          }
                        })()
                      : slot.start;
                    
                    const displayEnd = userRole === 'student' && booking?.tutorTimezone && booking?.studentTimezone && booking.tutorTimezone !== booking.studentTimezone
                      ? (() => {
                          try {
                            const dateString = selectedDate.toISOString().split('T')[0];
                            const dateTimeString = `${dateString}T${slot.end}:00`;
                            const tutorDate = fromZonedTime(dateTimeString, booking.tutorTimezone);
                            const studentDate = toZonedTime(tutorDate, booking.studentTimezone);
                            const hours = studentDate.getHours().toString().padStart(2, '0');
                            const minutes = studentDate.getMinutes().toString().padStart(2, '0');
                            return `${hours}:${minutes}`;
                          } catch {
                            return slot.end;
                          }
                        })()
                      : slot.end;
                    
                    return (
                      <Button
                        key={index}
                        variant={
                          selectedTimeSlot?.start === slot.start && selectedTimeSlot?.end === slot.end
                            ? "default"
                            : "outline"
                        }
                        onClick={() => setSelectedTimeSlot(slot)}
                        className="w-full"
                      >
                        {displayStart} - {displayEnd}
                      </Button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <AlertCircle className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">
                        No available time slots for this date
                      </p>
                    </div>
                  )}
                  {selectedTimeSlot && (
                    <div className="text-sm text-green-600 flex flex-col gap-1">
                      <div className="flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" />
                        <span>
                          Selected: {userRole === 'student' && booking?.tutorTimezone && booking?.studentTimezone && booking.tutorTimezone !== booking.studentTimezone
                            ? (() => {
                                try {
                                  const dateString = selectedDate.toISOString().split('T')[0];
                                  const startString = `${dateString}T${selectedTimeSlot.start}:00`;
                                  const endString = `${dateString}T${selectedTimeSlot.end}:00`;
                                  const tutorStartDate = fromZonedTime(startString, booking.tutorTimezone);
                                  const tutorEndDate = fromZonedTime(endString, booking.tutorTimezone);
                                  const studentStartDate = toZonedTime(tutorStartDate, booking.studentTimezone);
                                  const studentEndDate = toZonedTime(tutorEndDate, booking.studentTimezone);
                                  const startHours = studentStartDate.getHours().toString().padStart(2, '0');
                                  const startMinutes = studentStartDate.getMinutes().toString().padStart(2, '0');
                                  const endHours = studentEndDate.getHours().toString().padStart(2, '0');
                                  const endMinutes = studentEndDate.getMinutes().toString().padStart(2, '0');
                                  return `${startHours}:${startMinutes} - ${endHours}:${endMinutes}`;
                                } catch {
                                  return `${selectedTimeSlot.start} - ${selectedTimeSlot.end}`;
                                }
                              })()
                            : `${selectedTimeSlot.start} - ${selectedTimeSlot.end}`
                          }
                        </span>
                      </div>
                      {viewerTimezone && (
                        <span className="text-xs ml-4">({viewerTimezone})</span>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Reason (optional) */}
          <div className="space-y-2">
            <Label htmlFor="reason">Reason (Optional)</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why you need to reschedule..."
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={
                !selectedDate || 
                isSubmitting ||
                (userRole === 'tutor' ? (!manualStartTime || !manualEndTime) : !selectedTimeSlot)
              }
            >
              {isSubmitting ? 'Sending...' : 'Send Request'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
